import { useMemo, useEffect, useRef, useState } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { getSidebarConfigForAction } from './utils.js';

export function HtmlContent({ html, className }) {
  return <div className={className} dangerouslySetInnerHTML={{ __html: html || '' }} />;
}

export function getSortIcons(sort) {
  if (!sort) {
    return null;
  }

  if (sort.current) {
    return sort.dir === 'ASC' ? '▾' : '▴';
  }

  return sort.dir === 'ASC' ? '▿' : '▵';
}

export function SortHeader({ column }) {
  if (!column?.sort?.url) {
    return <span>{column.label}</span>;
  }

  const icon = getSortIcons(column.sort);

  return (
    <a href={column.sort.url} className="cat-react-table__sort-link">
      <span className="cat-react-table__sort-icon" aria-hidden="true">{icon}</span>
      <span>{column.label}</span>
      <span className="cat-react-table__sort-icon" aria-hidden="true">{icon}</span>
    </a>
  );
}

let actionMenuId = 0;

// ── Form-modal actions ──────────────────────────────────────────────────────
// Mirrors templates/components/tables/cst-admin-tools-form-modal-action.html.twig
// and js/cst-admin-tools-form-modal-action.js. The React listing replaces the
// server-rendered table, so the modal definition must be rendered here too —
// otherwise a modal action degrades into a plain <a href> to a POST-only route.

function ModalCheckboxes({ field, entityId, hydrate }) {
  const hasStatic = !!field.options && !field.options_url;
  const [options, setOptions] = useState(
    hasStatic
      ? Object.entries(field.options).map(([value, label]) => ({
          value,
          label,
          checked: !!field.default_checked,
        }))
      : null,
  );
  const [status, setStatus] = useState(hasStatic ? 'ready' : 'idle');

  useEffect(() => {
    if (!field.options_url || !hydrate || status !== 'idle') {
      return;
    }
    setStatus('loading');
    const sep = field.options_url.indexOf('?') === -1 ? '?' : '&';
    fetch(`${field.options_url}${sep}id=${encodeURIComponent(entityId ?? '')}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((payload) => {
        setOptions((payload?.options || []).map((opt) => ({
          value: opt.value,
          label: opt.label || `#${opt.value}`,
          checked: opt.checked !== undefined ? !!opt.checked : !!field.default_checked,
        })));
        setStatus('ready');
      })
      .catch(() => {
        setOptions([]);
        setStatus('error');
      });
  }, [field.options_url, field.default_checked, hydrate, status, entityId]);

  return (
    <div
      className="cst-modal-checkboxes"
      data-cst-checkboxes-name={field.name}
      data-cst-checkboxes-required={field.required ? '1' : undefined}
    >
      {status === 'loading' ? <div className="cst-modal-checkboxes__loading text-muted small">Chargement…</div> : null}
      {status === 'error' ? <p className="text-danger small mb-0">Erreur lors du chargement des options.</p> : null}
      {status === 'ready' && options && options.length === 0 ? (
        <p className="text-muted small mb-0">Aucune option disponible.</p>
      ) : null}
      {(options || []).map((opt, index) => {
        const optionId = `${field.name}-opt-${index}`;
        return (
          <div className="form-check" key={optionId}>
            <input
              className="form-check-input"
              type="checkbox"
              name={field.name}
              value={String(opt.value)}
              defaultChecked={opt.checked}
              id={optionId}
            />
            <label className="form-check-label" htmlFor={optionId}>{opt.label}</label>
          </div>
        );
      })}
    </div>
  );
}

// Read-only HTML block hydrated from a server-rendered fragment (e.g. the
// "users granted this role" table). Mirrors the vanilla + Twig 'html' field.
function ModalHtml({ field, entityId, hydrate }) {
  const [html, setHtml] = useState('<p class="text-muted small mb-0">Chargement…</p>');
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    if (!field.data_url || !hydrate || status !== 'idle') {
      return;
    }
    setStatus('loading');
    const sep = field.data_url.indexOf('?') === -1 ? '?' : '&';
    fetch(`${field.data_url}${sep}id=${encodeURIComponent(entityId ?? '')}`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((markup) => { setHtml(markup); setStatus('ready'); })
      .catch(() => { setHtml('<p class="text-danger small mb-0">Erreur lors du chargement.</p>'); setStatus('error'); });
  }, [field.data_url, hydrate, status, entityId]);

  // eslint-disable-next-line react/no-danger
  return <div className="cst-modal-html" dangerouslySetInnerHTML={{ __html: html }} />;
}

function ModalField({ field, entityId, hydrate, fieldId }) {
  const label = field.label ? (
    <label htmlFor={fieldId} className="form-label">
      {field.label}
      {field.required ? <span className="text-danger"> *</span> : null}
    </label>
  ) : null;

  let control = null;
  if (field.type === 'textarea') {
    control = (
      <textarea
        id={fieldId}
        name={field.name}
        rows={field.rows || 3}
        className="form-control"
        placeholder={field.placeholder || ''}
        required={!!field.required}
      />
    );
  } else if (field.type === 'text') {
    control = (
      <input
        type="text"
        id={fieldId}
        name={field.name}
        className="form-control"
        placeholder={field.placeholder || ''}
        required={!!field.required}
      />
    );
  } else if (field.type === 'checkboxes') {
    control = <ModalCheckboxes field={field} entityId={entityId} hydrate={hydrate} />;
  } else if (field.type === 'html') {
    control = <ModalHtml field={field} entityId={entityId} hydrate={hydrate} />;
  } else {
    return null;
  }

  return (
    <div className="mb-3">
      {label}
      {control}
    </div>
  );
}

export function FormModalAction({ mf }) {
  const modalRef = useRef(null);
  const [hydrate, setHydrate] = useState(false);

  useEffect(() => {
    const element = modalRef.current;
    if (!element) {
      return undefined;
    }
    const onShow = () => setHydrate(true);
    element.addEventListener('show.bs.modal', onShow);
    return () => element.removeEventListener('show.bs.modal', onShow);
  }, []);

  function handleSubmit(event) {
    const form = event.currentTarget;
    const required = form.querySelectorAll('.cst-modal-checkboxes[data-cst-checkboxes-required="1"]');
    for (let i = 0; i < required.length; i += 1) {
      if (required[i].querySelectorAll('input[type="checkbox"]:checked').length === 0) {
        event.preventDefault();
        required[i].classList.add('is-invalid');
        const name = required[i].dataset.cstCheckboxesName || 'options';
        window.alert(`Sélectionnez au moins une option pour "${name}".`);
        return;
      }
      required[i].classList.remove('is-invalid');
    }
  }

  // Portal to <body> so the modal escapes any ancestor stacking context
  // (the listing shell creates one), matching Bootstrap's recommended placement
  // and letting the backdrop cover the whole page including the top nav.
  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const modal = (
    <div
      ref={modalRef}
      className="modal fade"
      id={mf.id}
      tabIndex="-1"
      aria-labelledby={`${mf.id}-title`}
      aria-hidden="true"
      data-cst-form-modal-action="1"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <form method="POST" action={mf.action_url} data-cst-form-modal-form="1" onSubmit={handleSubmit}>
            <div className="modal-header">
              <h1 className="modal-title fs-5" id={`${mf.id}-title`}>{mf.title}</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              {mf.entity_id != null ? <input type="hidden" name="id" value={mf.entity_id} /> : null}
              {(mf.fields || []).map((field, index) => (
                <ModalField
                  key={`${mf.id}-f-${index + 1}`}
                  field={field}
                  entityId={mf.entity_id}
                  hydrate={hydrate}
                  fieldId={`${mf.id}-f-${index + 1}`}
                />
              ))}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">
                {mf.cancel_label || 'Annuler'}
              </button>
              {mf.show_submit === false ? null : (
                <button type="submit" className={`btn ${mf.submit_class || 'btn-primary'}`}>
                  {mf.submit_label}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return portalTarget ? createPortal(modal, portalTarget) : modal;
}

export function ActionMenu({
  items,
  buttonClassName,
  buttonStyle,
  buttonContent,
  buttonLabel,
  buttonTitle,
  menuClassName = '',
}) {
  const shouldReduceMotion = useReducedMotion();
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({ visibility: 'hidden' });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuIdRef = useRef('');
  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  const opensUpwards = String(menuStyle.transformOrigin || '').includes('bottom');
  const menuTransition = shouldReduceMotion
    ? { duration: 0 }
    : { duration: 0.16, ease: [0.22, 1, 0.36, 1] };

  if (!items.length) {
    return null;
  }

  if (!menuIdRef.current) {
    actionMenuId += 1;
    menuIdRef.current = `cat-crud-action-menu-${actionMenuId}`;
  }

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;

      if (!trigger || !menu) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 8;
      const margin = 12;
      const maxHeightBelow = viewportHeight - triggerRect.bottom - gap - margin;
      const maxHeightAbove = triggerRect.top - gap - margin;
      const shouldOpenUpwards = maxHeightBelow < Math.min(menuRect.height, 240) && maxHeightAbove > maxHeightBelow;

      let left = triggerRect.right - menuRect.width;
      let top = shouldOpenUpwards ? triggerRect.top - gap - menuRect.height : triggerRect.bottom + gap;

      left = Math.max(margin, Math.min(left, viewportWidth - menuRect.width - margin));
      top = Math.max(margin, Math.min(top, viewportHeight - menuRect.height - margin));

      setMenuStyle({
        top: `${top}px`,
        left: `${left}px`,
        maxHeight: `${Math.max(96, shouldOpenUpwards ? maxHeightAbove : maxHeightBelow)}px`,
        transformOrigin: shouldOpenUpwards ? 'bottom right' : 'top right',
      });
    };

    const handlePointerDown = (event) => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;

      if ((trigger && trigger.contains(event.target)) || (menu && menu.contains(event.target))) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      setIsOpen(false);
      triggerRef.current?.focus();
    };

    setMenuStyle({ visibility: 'hidden' });
    updatePosition();

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={buttonClassName}
        style={buttonStyle}
        title={buttonTitle || buttonLabel}
        aria-label={buttonLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuIdRef.current}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen((previous) => !previous);
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {buttonContent}
      </button>
      {portalTarget ? createPortal(
        <AnimatePresence>
          {isOpen ? (
            <motion.div
              ref={menuRef}
              id={menuIdRef.current}
              className={`cat-crud-action-menu__popover ${menuClassName}`.trim()}
              style={menuStyle}
              role="menu"
              aria-label={buttonLabel}
              initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: opensUpwards ? 8 : -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: opensUpwards ? 4 : -4 }}
              transition={menuTransition}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {items.map((item) => (
                item.modalForm ? (
                  <button
                    key={`${item.label}-${item.modalForm.id}`}
                    type="button"
                    className="cat-crud-action-menu__item"
                    data-bs-toggle="modal"
                    data-bs-target={`#${item.modalForm.id}`}
                    data-cst-modal-open={item.modalForm.id}
                    role="menuitem"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </button>
                ) : (
                  <a
                    key={`${item.url}-${item.label}`}
                    className="cat-crud-action-menu__item"
                    href={item.url}
                    role="menuitem"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </a>
                )
              ))}
            </motion.div>
          ) : null}
        </AnimatePresence>,
        portalTarget,
      ) : null}
    </>
  );
}

export function SidebarActionsCell({ row, ui, directNavigationKinds = [] }) {
  const actions = row.sidebarActions || {};
  const viewAction = actions.view;
  const editAction = actions.edit;
  const deleteAction = actions.delete;
  const otherActions = actions.others || [];
  const iconOnlyActions = otherActions.filter((item) => item?.iconOnly && !item?.modalForm);
  const iconModalActions = otherActions.filter((item) => item?.iconOnly && item?.modalForm);
  const menuActions = otherActions.filter((item) => !item?.iconOnly);
  const modalActions = otherActions.filter((item) => item?.modalForm);
  const viewSidebarConfig = getSidebarConfigForAction(viewAction);
  const editSidebarConfig = getSidebarConfigForAction(editAction);
  const viewDirect = directNavigationKinds.includes('view');
  const editDirect = directNavigationKinds.includes('edit');
  const getIconOnlyActionVariant = (type = '') => {
    if (type === 'btn-success ') return 'success';
    if (type === 'btn-danger ') return 'danger';
    if (type === 'btn-primary ') return 'primary';
    return 'default';
  };

  return (
    <div className="cat-crud-actions" role="group" aria-label={ui.actionsLabel}>
      {viewAction ? (
        <a
          href={viewAction.url}
          className="cat-crud-action-icon"
          style={{ gridColumn: 1 }}
          title={ui.viewLabel}
          aria-label={ui.viewLabel}
          {...(!viewDirect && {
            'data-cst-sidebar-trigger': 'view',
            'data-cst-sidebar-label': row.identifier || '',
            'data-cst-sidebar-schema-url': viewSidebarConfig.schemaUrl,
            'data-cst-sidebar-page-url': viewSidebarConfig.pageUrl,
            'data-cst-sidebar-fullscreen-url': viewSidebarConfig.pageUrl,
            'data-cst-sidebar-fallback-url': viewSidebarConfig.fallbackUrl,
            'data-cst-sidebar-edit-schema-url': editSidebarConfig.schemaUrl,
            'data-cst-sidebar-edit-page-url': editSidebarConfig.pageUrl,
            'data-cst-sidebar-edit-fallback-url': editSidebarConfig.fallbackUrl,
          })}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/>
          </svg>
        </a>
      ) : null}

      {editAction ? (
        <a
          href={editAction.url}
          className="cat-crud-action-icon"
          style={{ gridColumn: 2 }}
          title={ui.editLabel}
          aria-label={ui.editLabel}
          {...(!editDirect && {
            'data-cst-sidebar-trigger': 'edit',
            'data-cst-sidebar-label': row.identifier || '',
            'data-cst-sidebar-schema-url': editSidebarConfig.schemaUrl,
            'data-cst-sidebar-page-url': editSidebarConfig.pageUrl,
            'data-cst-sidebar-fullscreen-url': editSidebarConfig.pageUrl,
            'data-cst-sidebar-fallback-url': editSidebarConfig.fallbackUrl,
            'data-cst-sidebar-edit-schema-url': '',
            'data-cst-sidebar-edit-page-url': '',
            'data-cst-sidebar-edit-fallback-url': '',
          })}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/>
          </svg>
        </a>
      ) : null}

      {deleteAction ? (
        <a
          href={deleteAction.url}
          className="cat-crud-action-icon cat-crud-action-icon--danger"
          style={{ gridColumn: 3 }}
          title={ui.deleteLabel}
          aria-label={ui.deleteLabel}
          data-cst-delete-trigger=""
          data-cst-delete-url={deleteAction.url || ''}
          data-cst-delete-label={row.identifier || ''}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
        </a>
      ) : null}

      {iconOnlyActions.map((item, index) => (
        <a
          key={`${item.url}-${item.label}-${index}`}
          href={item.url}
          className={`cat-crud-action-icon-btn cat-crud-action-icon-btn--${getIconOnlyActionVariant(item.type)}`}
          style={{ gridColumn: 4 }}
          title={item.label}
          aria-label={item.label}
        >
          <i className={`fa fa-${item.icon || 'ellipsis'}`} />
        </a>
      ))}

      {iconModalActions.map((item, index) => (
        <button
          key={`${item.modalForm.id}-${index}`}
          type="button"
          className={`cat-crud-action-icon-btn cat-crud-action-icon-btn--${getIconOnlyActionVariant(item.type)}`}
          style={{ gridColumn: 4 }}
          data-bs-toggle="modal"
          data-bs-target={`#${item.modalForm.id}`}
          data-cst-modal-open={item.modalForm.id}
          title={item.label}
          aria-label={item.label}
        >
          <i className={`fa fa-${item.icon || 'ellipsis'}`} />
        </button>
      ))}

      {menuActions.length ? (
        <ActionMenu
          items={menuActions}
          buttonClassName="cat-crud-action-icon"
          buttonStyle={{ gridColumn: 4 }}
          buttonLabel={ui.actionsLabel}
          buttonTitle={ui.actionsLabel}
          buttonContent={(
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/>
            </svg>
          )}
        />
      ) : null}

      {modalActions.map((item) => (
        <FormModalAction key={item.modalForm.id} mf={item.modalForm} />
      ))}
    </div>
  );
}

export function DropdownActionsCell({ row, ui }) {
  const actionItems = Object.values(row.actions || {}).filter((item) => item && item.actionType === 'btn');

  if (!actionItems.length) {
    return null;
  }

  return (
    <ActionMenu
      items={actionItems}
      buttonClassName="btn btn-secondary btn-sm cat-crud-action-trigger"
      buttonLabel={ui.actionsLabel}
      buttonContent={ui.actionsLabel}
      menuClassName="cat-react-table__dropdown-popover"
    />
  );
}

export function DefaultActionCell({ item }) {
  if (!item || item.actionType !== 'btn') {
    return null;
  }

  const additionalClass = item.additionalClass ? ` ${item.additionalClass}` : '';

  return (
    <a className={`btn btn-${item.type} btn-sm w-100${additionalClass}`} href={item.url} role="button">
      {item.label}
    </a>
  );
}

export function handleRowSidebarCellClick(event) {
  const cell = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
  const rowTrigger = cell?.closest?.('[data-cst-row-sidebar-trigger]');
  if (!rowTrigger) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (window.CstAdminToolsCrudSidebar && typeof window.CstAdminToolsCrudSidebar.openFromTrigger === 'function') {
    window.CstAdminToolsCrudSidebar.openFromTrigger(rowTrigger);
  }
}

function useSyncRowHeights(leftRef, centerRef, rightRef, rowCount) {
  useEffect(() => {
    const left = leftRef.current;
    const center = centerRef.current;
    const right = rightRef.current;
    if (!center) {
      return;
    }

    const tables = [left, center, right].filter(Boolean);
    // Reset heights so we measure natural sizes
    tables.forEach((t) => {
      t.querySelectorAll('tr').forEach((tr) => { tr.style.height = ''; });
    });

    const rowSets = tables.map((t) => Array.from(t.querySelectorAll('tr')));
    const count = Math.max(...rowSets.map((rs) => rs.length));

    for (let i = 0; i < count; i++) {
      let max = 0;
      rowSets.forEach((rs) => {
        if (rs[i]) {
          max = Math.max(max, rs[i].offsetHeight);
        }
      });
      rowSets.forEach((rs) => {
        if (rs[i]) {
          rs[i].style.height = `${max}px`;
        }
      });
    }
  }, [leftRef, centerRef, rightRef, rowCount]);
}

function renderCells(row, columnIds, payload, rowData) {
  const rowSidebarConfig = getSidebarConfigForAction(rowData.rowClickAction);
  const rowEditSidebarConfig = getSidebarConfigForAction(rowData.sidebarActions?.edit);

  return row.getVisibleCells()
    .filter((cell) => columnIds.has(cell.column.id))
    .map((cell) => {
      const cellClassName = cell.column.columnDef.meta?.cellClassName || '';
      const isRowClickableCell = Boolean(rowData.rowClickAction)
        && cell.column.id !== 'select'
        && cell.column.id !== '__actions';
      const cellProps = {
        className: cellClassName,
        'data-column-id': cell.column.id,
      };

      if (rowData.rowClickAction) {
        if (isRowClickableCell) {
          cellProps['data-cst-row-click-cell'] = '1';
          cellProps.onClick = handleRowSidebarCellClick;
        } else {
          cellProps['data-cst-ignore-row-click'] = '1';
        }
      }

      if (cell.column.columnDef.meta?.isRowHeader) {
        return (
          <th key={cell.id} scope="row" {...cellProps}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </th>
        );
      }

      return (
        <td key={cell.id} {...cellProps}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      );
    });
}

function renderHeaders(headerGroups, columnIds) {
  return headerGroups.map((headerGroup) => (
    <tr key={headerGroup.id}>
      {headerGroup.headers
        .filter((header) => columnIds.has(header.column.id))
        .map((header) => {
          const headerClassName = header.column.columnDef.meta?.headerClassName || '';
          return (
            <th key={header.id} scope="col" className={headerClassName} data-column-id={header.column.id}>
              {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
            </th>
          );
        })}
    </tr>
  ));
}

function buildRowProps(rowData) {
  const rowSidebarConfig = getSidebarConfigForAction(rowData.rowClickAction);
  const rowEditSidebarConfig = getSidebarConfigForAction(rowData.sidebarActions?.edit);
  const rowClassName = `crud-select-row ${rowData.rowClass || ''}${rowData.rowClickAction ? ' cat-crud-row-trigger' : ''}`.trim();

  const props = rowData.rowClickAction ? {
    'data-cst-row-sidebar-trigger': '1',
    'data-cst-sidebar-trigger': rowData.rowClickAction.kind || 'view',
    'data-cst-sidebar-label': rowData.identifier || '',
    'data-cst-sidebar-schema-url': rowSidebarConfig.schemaUrl,
    'data-cst-sidebar-page-url': rowSidebarConfig.pageUrl,
    'data-cst-sidebar-fullscreen-url': rowSidebarConfig.pageUrl,
    'data-cst-sidebar-fallback-url': rowSidebarConfig.fallbackUrl,
    'data-cst-sidebar-edit-schema-url': rowEditSidebarConfig.schemaUrl,
    'data-cst-sidebar-edit-page-url': rowEditSidebarConfig.pageUrl,
    'data-cst-sidebar-edit-fallback-url': rowEditSidebarConfig.fallbackUrl,
  } : {};

  return { className: rowClassName, ...props };
}

export function ListingApp({ payload }) {
  const columns = useMemo(() => {
    const baseColumns = (payload.columns || []).map((column) => {
      if (column.kind === 'select') {
        return {
          id: column.id,
          header: () => null,
          cell: ({ row }) => (
            <HtmlContent
              className="form-check cat-crud-select-control"
              html={row.original.selectControlHtml}
            />
          ),
          meta: {
            headerClassName: 'cat-crud-select-cell',
            cellClassName: 'cat-crud-select-cell',
            isRowHeader: true,
          },
        };
      }

      return {
        id: column.id,
        header: () => <SortHeader column={column} />,
        cell: ({ row }) => (
          <HtmlContent
            className="cat-react-table__cell-html"
            html={row.original.cells?.[column.id] || ''}
          />
        ),
        meta: {
          headerClassName: 'align-middle',
          cellClassName: 'align-middle',
        },
      };
    });

    if (payload.mode === 'sidebar') {
      baseColumns.push({
        id: '__actions',
        header: () => null,
        cell: ({ row }) => <SidebarActionsCell row={row.original} ui={payload.ui} directNavigationKinds={payload.directNavigationKinds || []} />,
        meta: {
          headerClassName: 'align-middle',
          cellClassName: 'align-middle cat-crud-actions-cell',
        },
      });
      return baseColumns;
    }

    if (payload.mode === 'dropdown') {
      baseColumns.push({
        id: '__actions',
        header: () => null,
        cell: ({ row }) => <DropdownActionsCell row={row.original} ui={payload.ui} />,
        meta: {
          headerClassName: 'align-middle',
          cellClassName: 'align-middle cat-crud-actions-cell',
        },
      });
      return baseColumns;
    }

    (payload.actionHeaders || []).forEach((column) => {
      baseColumns.push({
        id: column.id,
        header: () => column.label || '',
        cell: ({ row }) => <DefaultActionCell item={row.original.actions?.[column.id]} />,
        meta: {
          headerClassName: 'align-middle',
          cellClassName: 'align-middle cat-react-table__action-cell',
        },
      });
    });

    return baseColumns;
  }, [payload]);

  const table = useReactTable({
    data: payload.rows || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Split columns into left-pinned, center (scrollable), right-pinned
  const hasSelect = columns.length > 0 && columns[0].id === 'select';
  const lastCol = columns[columns.length - 1];
  const hasRightPin = lastCol && lastCol.id !== 'select' && columns.length > 2;

  const leftIds = useMemo(() => new Set(hasSelect ? ['select'] : []), [hasSelect]);
  const rightIds = useMemo(() => new Set(hasRightPin ? [lastCol.id] : []), [hasRightPin, lastCol]);
  const centerIds = useMemo(() => {
    const ids = new Set();
    columns.forEach((col) => {
      if (!leftIds.has(col.id) && !rightIds.has(col.id)) {
        ids.add(col.id);
      }
    });
    return ids;
  }, [columns, leftIds, rightIds]);

  const leftTableRef = useRef(null);
  const centerTableRef = useRef(null);
  const rightTableRef = useRef(null);

  const rows = table.getRowModel().rows;
  useSyncRowHeights(leftTableRef, centerTableRef, rightTableRef, rows.length);

  const headerGroups = table.getHeaderGroups();
  const tableClass = 'table table-striped table-hover table-fit table-sm cat-react-listing__table';

  return (
    <div className="cat-react-listing">
      <div className="cat-react-listing__split">
        {/* Left pinned panel (checkbox) */}
        {hasSelect ? (
          <div className="cat-react-listing__panel cat-react-listing__panel--left">
            <table ref={leftTableRef} className={tableClass}>
              <thead className="cat-react-listing__head">
                {renderHeaders(headerGroups, leftIds)}
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className={buildRowProps(row.original).className}>
                    {renderCells(row, leftIds, payload, row.original)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {/* Center scrollable panel */}
        <div className="cat-react-listing__panel cat-react-listing__panel--center">
          <table ref={centerTableRef} className={tableClass}>
            <thead className="cat-react-listing__head">
              {renderHeaders(headerGroups, centerIds)}
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id} {...buildRowProps(row.original)}>
                  {renderCells(row, centerIds, payload, row.original)}
                </tr>
              )) : (
                <tr>
                  <td colSpan={centerIds.size || 1} className="cat-react-listing__empty">
                    {payload.ui.emptyLabel}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Right pinned panel (actions) */}
        {hasRightPin ? (
          <div className="cat-react-listing__panel cat-react-listing__panel--right">
            <table ref={rightTableRef} className={tableClass}>
              <thead className="cat-react-listing__head">
                {renderHeaders(headerGroups, rightIds)}
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className={buildRowProps(row.original).className}>
                    {renderCells(row, rightIds, payload, row.original)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
